import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import { MONO_FONT, PillTabs, headCellSx, numCellSx, textCellSx } from "../../components/metrics/metricsUi";
import { purchaseOrderAnalysisService } from "../../utils/services/purchaseOrderAnalysisService";
import {
  PO_GROUP_TABS,
  STAGE_LABEL,
  TIMING_LABEL,
  buildRowsParams,
  calendarLabel,
  coverageColor,
  fmtShortDay,
  fmtUnits,
} from "../../utils/purchaseOrderAnalysisPageDefaults";
import { FilterPill, ProgressBar, SectionCard, StatusChip, useLatestRequest } from "./parts";

const SEARCH_DEBOUNCE_MS = 300;

const COLUMNS = [
  { id: "po_no", label: "PO", sort: "po_no", width: 150 },
  { id: "supplier_name", label: "Supplier" },
  { id: "po_date", label: "PO date", sort: "po_date", width: 96 },
  { id: "expected_date", label: "Expected", sort: "expected_date", width: 110 },
  { id: "stage", label: "Stage", width: 132 },
  { id: "timing", label: "Timing", width: 128 },
  { id: "lines", label: "SKUs", num: true, width: 64 },
  { id: "units_ordered", label: "Ordered", sort: "units_ordered", num: true, width: 88 },
  { id: "received", label: "Received", width: 140 },
  { id: "units_outstanding", label: "Outstanding", sort: "units_outstanding", num: true, width: 104 },
];

// Timing → status colour (a stale PO reads like "not counted" supply).
const TIMING_COLOR = { scheduled: "covered", overdue: "late", unconfirmed: "unconfirmed", no_date: "pending" };

const PoTable = ({ ui, base, group, onGroup, bucket, onClearBucket, calendar, onOpenPo, mobile, id }) => {
  const { colors } = ui;
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "po_date", dir: "desc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [group, bucket, base.warehouse_id, base.months]);

  const rows = useLatestRequest(
    base.warehouse_id
      ? (signal) =>
          purchaseOrderAnalysisService.fetchRows(
            buildRowsParams({ base, table: "pos", page, rowsPerPage, sort: sort.field, dir: sort.dir, search, group, bucket }),
            { signal },
          )
      : null,
    [base.warehouse_id, base.client_id, base.months, page, rowsPerPage, sort, search, group, bucket],
  );
  const data = rows.data;
  const bucketInfo = bucket ? calendar.find((item) => item.key === bucket) : null;

  const toggleSort = (field) =>
    setSort((current) =>
      current.field === field ? { field, dir: current.dir === "desc" ? "asc" : "desc" } : { field, dir: "desc" },
    );

  const cell = (row, column) => {
    switch (column.id) {
      case "po_no":
        return <Box sx={{ fontFamily: MONO_FONT, fontWeight: 650, fontSize: 12.5 }}>{row.po_no || "—"}</Box>;
      case "supplier_name":
        return (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.supplier_name || "—"}</Box>
            <Box sx={{ fontSize: 11, color: colors.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.sku_preview.join(", ")}
              {row.lines > row.sku_preview.length ? ` +${row.lines - row.sku_preview.length}` : ""}
            </Box>
          </Box>
        );
      case "po_date":
        return fmtShortDay(row.po_date);
      case "expected_date":
        return row.expected_date ? (
          <Box sx={{ lineHeight: 1.3 }}>
            <Box>{fmtShortDay(row.expected_date)}</Box>
            {row.expected_kind === "dispatch" ? <Box sx={{ fontSize: 10.5, color: colors.textTertiary }}>dispatch</Box> : null}
          </Box>
        ) : (
          <Box component="span" sx={{ color: colors.textTertiary }}>No date</Box>
        );
      case "stage":
        return STAGE_LABEL[row.stage] || row.stage;
      case "timing":
        if (!row.timing) return <Box component="span" sx={{ color: colors.textTertiary }}>—</Box>;
        return (
          <StatusChip
            ui={ui}
            color={coverageColor(TIMING_COLOR[row.timing], ui.darkMode)}
            label={row.days_overdue ? `${TIMING_LABEL[row.timing]} ${row.days_overdue}d` : TIMING_LABEL[row.timing]}
          />
        );
      case "received":
        return row.received_reported ? (
          <ProgressBar ui={ui} value={row.units_received} total={row.units_ordered} />
        ) : (
          <Box component="span" sx={{ fontSize: 11.5, color: colors.textTertiary }}>Not reported</Box>
        );
      default:
        return fmtUnits(row[column.id]);
    }
  };

  return (
    <SectionCard
      id={id}
      ui={ui}
      title="Purchase orders"
      subtitle="POs with at least one line landing at this warehouse; units count this warehouse's lines only. Click a PO for its lines, deliveries and status trail."
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", mb: 1.25 }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <PillTabs
            tabs={PO_GROUP_TABS.map((tab) => ({ ...tab, badge: data?.counts ? fmtUnits(data.counts[tab.value]) : undefined }))}
            value={group}
            onChange={onGroup}
          />
          {bucketInfo ? (
            <FilterPill ui={ui} active label={`Calendar: ${calendarLabel(bucketInfo)} ✕`} onClick={onClearBucket} />
          ) : null}
        </Box>
        <TextField
          size="small"
          placeholder="Search PO number or SKU…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ width: mobile ? "100%" : 260, m: 0, "& .MuiOutlinedInput-root": { height: 36, fontSize: 13.5 } }}
        />
      </Box>
      <Box sx={{ overflowX: "auto", opacity: rows.loading ? 0.6 : 1, transition: "opacity 120ms" }}>
        <Table size="small" sx={{ tableLayout: "fixed", minWidth: 1100 }}>
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.id} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column.id} sx={headCellSx(ui, { textAlign: column.num ? "right" : "left", whiteSpace: "nowrap", px: 1.25 })}>
                  {column.sort ? (
                    <TableSortLabel
                      active={sort.field === column.sort}
                      direction={sort.field === column.sort ? sort.dir : "desc"}
                      onClick={() => toggleSort(column.sort)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.error ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={textCellSx(ui, { py: 3, color: ui.danger })}>{rows.error}</TableCell>
              </TableRow>
            ) : !data || !data.rows.length ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={textCellSx(ui, { py: 3 })}>
                  {rows.loading ? "Loading…" : "No purchase orders match."}
                </TableCell>
              </TableRow>
            ) : (
              data.rows.map((row) => (
                <TableRow
                  key={row.po_id}
                  tabIndex={0}
                  onClick={() => onOpenPo(row.po_id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onOpenPo(row.po_id);
                  }}
                  sx={{ cursor: "pointer", "&:hover": { backgroundColor: ui.surfaceAlt } }}
                >
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={
                        column.num
                          ? numCellSx(ui, { fontFamily: MONO_FONT, whiteSpace: "nowrap", px: 1.25 })
                          : textCellSx(ui, { px: 1.25 })
                      }
                    >
                      {cell(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_event, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </SectionCard>
  );
};

export default PoTable;
